import React, { useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useLocation, useNavigate } from "react-router-dom";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { TableContainer } from "@mui/material";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import DocumentViewerDialog from "./DocumentViewerDialog";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import DirectionsCarFilledRoundedIcon from "@mui/icons-material/DirectionsCarFilledRounded";
import HouseIcon from "@mui/icons-material/House";

import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";

const PRIMARY = "#7B6ED6";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";

export default function VehicleMaster() {
  const location = useLocation();
  const preloadVehicleId = location.state?.preloadVehicleId; // id passed from ReminderMaster
  const returnTo = location.state?.returnTo;
  // The store this screen works in. location.state carries it when the screen
  // is opened from inside the app; on a direct load or an F5 there is no router
  // state at all, so fall back to the session the way DriverMaster does — an
  // unguarded read here white-screened the route.
  const { userInfo, currentStoreInfo } = useContext(AutoTaskAuthContext) || {};
  const userStoreID =
    location.state?.store_id ?? currentStoreInfo?.id ?? userInfo?.store_id;
  const navigate = useNavigate();

  // Responsive Layout Parameters
  const resTheme = useTheme();
  const isMobile = useMediaQuery(resTheme.breakpoints.down("md"));

  const topRef = useRef(null);

  const emptyVehicleForm = {
    vehicle_code: "",
    vehicle_sl_no: "",
    vehicle_name: "",
    vehicle_number: "",
    vehicle_type: "",
    vehicle_status: "A",
    blocked_from: null,
    blocked_to: null,
    blocked_reason: "",
  };

  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [vehicleForm, setVehicleForm] = useState(emptyVehicleForm);
  const [search, setSearch] = useState("");
  const [isBlocking, setIsBlocking] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const emptyDocument = {
    name: "",
    number: "",
    file: null,
    fileName: "",
    renewalDate: "",
  };
  const [allVehicleTypes, setAllVehicleTypes] = useState([]);
  const [vehicleTypeSearch, setVehicleTypeSearch] = useState("");
  const [newlyCreatedTypes, setNewlyCreatedTypes] = useState(new Set());

  const [openDeleteDialogBox, setOpenDeleteDialogBox] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState(null);

  const [formData, setFormData] = useState({ documents: [] });
  const [newDocument, setNewDocument] = useState(emptyDocument);
  const [editingDocId, setEditingDocId] = useState(null);
  const [viewerDoc, setViewerDoc] = useState(null);

  const [selectedVehicleIds, setSelectedVehicleIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const [snackBarConfig, setSnackBarConfig] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const closeSnackbar = () =>
    setSnackBarConfig((prev) => ({ ...prev, open: false }));

  /* -------------------- FILTER -------------------- */
  const filteredVehicles = vehicles.filter((v) =>
    `${v.vehicle_number} ${v.vehicle_name}`
      .toLowerCase()
      .includes(search.toLowerCase()),
  );

  const handleToggleVehicle = (vehicle) => {
    setSelectedVehicleIds((prev) => {
      if (prev.includes(vehicle.vehicle_id)) {
        return prev.filter((id) => id !== vehicle.vehicle_id);
      }
      return [...prev, vehicle.vehicle_id];
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedVehicleIds([]);
      setSelectAll(false);
    } else {
      const allIds = filteredVehicles.map((v) => v.vehicle_id);
      setSelectedVehicleIds(allIds);
      setSelectAll(true);
    }
  };

  useEffect(() => {
    setSelectAll(
      filteredVehicles.length > 0 &&
        selectedVehicleIds.length === filteredVehicles.length,
    );
  }, [selectedVehicleIds, filteredVehicles]);

  useEffect(() => {
    if (selectedVehicle) {
      setVehicleForm({
        vehicle_code: selectedVehicle.vehicle_code,
        vehicle_sl_no: selectedVehicle.vehicle_sl_no,
        vehicle_name: selectedVehicle.vehicle_name,
        vehicle_number: selectedVehicle.vehicle_number,
        vehicle_type: selectedVehicle.vehicle_type,
        vehicle_status: selectedVehicle.status,
        blocked_from: selectedVehicle.blocked_from,
        blocked_to: selectedVehicle.blocked_to,
        blocked_reason: selectedVehicle.blocked_reason || "",
      });

      setFormData({
        documents: selectedVehicle.vehicle_documents || [],
      });

      setIsBlocking(selectedVehicle.vehicle_status === "B");
    } else {
      setVehicleForm(emptyVehicleForm);
      setFormData({ documents: [] });
      setIsBlocking(false);
    }
  }, [selectedVehicle]);

  useEffect(() => {
    fetchVehicles();
    fetchVehicleTypes();
  }, [userStoreID]);

  // Auto-load a specific vehicle when arriving from ReminderMaster
  useEffect(() => {
    if (preloadVehicleId) {
      fetchOneVehicle(preloadVehicleId);
    }
  }, [preloadVehicleId]);

  const getStatusText = (s) =>
    s === "A" ? "Active" : s === "I" ? "Inactive" : "Blocked";

  const getStatusColor = (s) =>
    s === "A" ? "success" : s === "I" ? "error" : "warning";

  // Reset function
  const resetAllForms = () => {
    setSelectedVehicle(null);
    setVehicleForm(emptyVehicleForm);
    setFormData({ documents: [] });
    setNewDocument(emptyDocument);
    setEditingDocId(null);
    setIsBlocking(false);

    // Scroll to top
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }, 100);
  };

  // Scrollbar remover for better UI
  const hideScrollbar = {
    overflowY: "auto",
    scrollbarWidth: "none", // Firefox
    "&::-webkit-scrollbar": {
      display: "none", // Chrome, Edge, Safari
    },
  };

  /* -------------------- DOCUMENT HANDLERS -------------------- */
  // ✅ NEW: Convert File to Base64
  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        resolve(reader.result); // This gives "data:image/png;base64,..."
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleDeleteDocument = (id) => {
    setFormData((p) => ({
      documents: p.documents.filter((d) => (d.id || d.doc_id) !== id),
    }));
  };

  const handleCancelEditDocument = () => {
    setEditingDocId(null);
    setNewDocument(emptyDocument);
  };

  const canCreateVehicleType =
    vehicleTypeSearch.trim().length > 0 &&
    !allVehicleTypes.some(
      (t) => t.toLowerCase() === vehicleTypeSearch.trim().toLowerCase(),
    );

  /* -------------------- APIs -------------------- */
  // GET All Vehicle Types
  const fetchVehicleTypes = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/travel/vehicles/types/${userStoreID}`,
      );
      if (res.data.success) setAllVehicleTypes(res.data.data ?? []);
    } catch {
      console.error("Failed to load vehicle types");
    }
  };

  // GET All Vehicles Data
  const fetchVehicles = async () => {
    try {
      setIsLoading(true);
      const res = await axios.get(
        `${API_BASE_URL}/api/travel/vehicles/store/${userStoreID}`,
      );
      setVehicles(res.data?.data || []);
    } catch {
      setSnackBarConfig({
        open: true,
        message: "Failed to load vehicles",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // GET One Vehicle Data
  const fetchOneVehicle = async (vehicleId) => {
    try {
      setIsLoading(true);

      const res = await axios.get(
        `${API_BASE_URL}/api/travel/vehicles/${vehicleId}`,
      );

      if (!res.data.success) throw res.data;

      const fetchedVehicle = res.data.data;

      setSelectedVehicle({
        vehicle_id: fetchedVehicle.vehicle_id,
        vehicle_code: fetchedVehicle.vehicle_code,
        vehicle_sl_no: fetchedVehicle.vehicle_sl_no,
        vehicle_name: fetchedVehicle.vehicle_name,
        vehicle_number: fetchedVehicle.vehicle_number,
        vehicle_type: fetchedVehicle.vehicle_type,
        vehicle_status: fetchedVehicle.vehicle_status,
        blocked_from: fetchedVehicle.blockedFrom,
        blocked_to: fetchedVehicle.blockedTo,
        blocked_reason: fetchedVehicle.blockedReason,
        vehicle_documents: (fetchedVehicle.vehicle_documents || []).map(
          (doc) => ({
            doc_id: doc.doc_id,
            name: doc.name,
            number: doc.number,
            file: doc.file,
            fileName: doc.fileName,
            renewalDate: doc.renewalDate ?? "",
          }),
        ),
      });

      // Scroll to top
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }, 100);
    } catch (error) {
      setSnackBarConfig({
        open: true,
        message: "Failed to load vehicle data",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Create/Update a vehicle
  const handleSubmit = async () => {
    if (isLoading) return;

    if (
      !vehicleForm.vehicle_name ||
      !vehicleForm.vehicle_number ||
      !vehicleForm.vehicle_sl_no
    ) {
      setSnackBarConfig({
        open: true,
        message: "Vehicle Name, Number and Serial Number are mandatory",
        severity: "warning",
      });
      return;
    }

    if (isBlocking) {
      if (!vehicleForm.blocked_from || !vehicleForm.blocked_to) {
        setSnackBarConfig({
          open: true,
          message: "Blocking dates are required",
          severity: "warning",
        });
        return;
      }
    }

    // ✅ Prepare documents payload
    const documentsPayload = formData.documents.map((doc) => {
      // For existing documents being updated
      if (doc.doc_id) {
        return {
          doc_id: doc.doc_id,
          name: doc.name,
          number: doc.number,
          file: doc.file, // This will be base64 or URL string
          fileName: doc.fileName,
          renewalDate: doc.renewalDate,
        };
      }
      // For new documents
      return {
        name: doc.name,
        number: doc.number,
        file: doc.file, // This will be base64 string
        fileName: doc.fileName,
        renewalDate: doc.renewalDate,
      };
    });

    const payload = {
      store_id: userStoreID,
      vehicle_code: vehicleForm.vehicle_number.slice(-4),
      vehicle_sl_no: Number(vehicleForm.vehicle_sl_no),
      vehicle_name: vehicleForm.vehicle_name,
      vehicle_number: vehicleForm.vehicle_number,
      type: vehicleForm.vehicle_type,
      status: isBlocking ? "B" : vehicleForm.vehicle_status,
      blockedFrom: isBlocking ? vehicleForm.blocked_from : null,
      blockedTo: isBlocking ? vehicleForm.blocked_to : null,
      blockedReason: isBlocking ? vehicleForm.blocked_reason : null,
      documents: documentsPayload,
    };

    // console.log("📤 Sending payload:", payload);

    try {
      setIsLoading(true);
      let vehicleAPIResponse;

      if (selectedVehicle) {
        vehicleAPIResponse = await axios.put(
          `${API_BASE_URL}/api/travel/vehicles/${selectedVehicle.vehicle_id}`,
          payload,
        );
      } else {
        vehicleAPIResponse = await axios.post(
          `${API_BASE_URL}/api/travel/vehicles/create`,
          payload,
        );
      }

      if (!vehicleAPIResponse.data.success) throw vehicleAPIResponse.data;

      fetchVehicles();
      resetAllForms();

      setSnackBarConfig({
        open: true,
        message:
          vehicleAPIResponse.data.statusMessage || "Vehicle saved successfully",
        severity: "success",
      });

      // If we came from ReminderMaster, navigate back so it can refresh documents
      if (returnTo) {
        setTimeout(() => {
          navigate(returnTo, { state: { fromMasterEdit: true } });
        }, 800); // small delay so the snackbar is visible before leaving
      }
    } catch (error) {
      console.error("❌ API Error:", error); // ✅ Debug log
      setSnackBarConfig({
        open: true,
        message: error?.statusMessage || "Operation failed",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // DELETE A Vehicle
  const handleConfirmDeleteVehicle = async () => {
    try {
      setIsLoading(true);

      const payload = Array.isArray(vehicleToDelete)
        ? { vehicle_ids: vehicleToDelete.map((v) => v.vehicle_id) }
        : null;

      const url = Array.isArray(vehicleToDelete)
        ? `${API_BASE_URL}/api/travel/vehicles/bulk-delete`
        : `${API_BASE_URL}/api/travel/vehicles/${vehicleToDelete.vehicle_id}`;

      const res = await axios.delete(url, {
        data: payload,
      });

      if (!res.data.success) throw res.data;

      setSnackBarConfig({
        open: true,
        message: res.data.statusMessage,
        severity: "success",
      });

      fetchVehicles();
      resetAllForms();
      setSelectedVehicleIds([]);
    } catch (error) {
      setSnackBarConfig({
        open: true,
        message: error?.statusMessage || "Failed to delete vehicle(s)",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
      setOpenDeleteDialogBox(false);
      setVehicleToDelete(null);
    }
  };

  /* -------------------- UI -------------------- */
  return (
    <Box p={isMobile ? 1.5 : 3} sx={{ background: "#f7f7f9" }}>
      {/* Header for Mobiles on the top */}
      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          alignItems: "center",
          gap: 1.5,
          mb: 2,
          pb: 1.5,
          borderBottom: `1px solid ${PRIMARY}20`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            onClick={() => navigate(-1)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              borderRadius: 2,
              bgcolor: "#139fcae8",
              p: 0.5,
            }}
          >
            <ArrowBackRoundedIcon sx={{ fill: "#FFF" }} />
          </Box>
          <Box
            onClick={() => navigate("/travel/dashboard")}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              borderRadius: 2,
              bgcolor: "#139fcae8",
              p: 0.5,
            }}
          >
            <HouseIcon sx={{ fill: "#FFF" }} />
          </Box>
          <Typography
            variant="body1"
            fontWeight={600}
            sx={{ color: "#139fcae8" }}
          >
            Vehicle Master
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={2}>
        {/* LEFT */}
        <Grid
          item
          xs={12}
          md={5}
          sx={{
            height: isMobile ? "auto" : "calc(100vh - 120px)",
            display: "flex",
            flexDirection: "column",
            order: { xs: 2, md: 1 },
          }}
        >
          {/* Header for Desktops inside left panel */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              gap: 1.5,
              mb: 1.5,
            }}
          >
            <Box
              onClick={() => navigate(-1)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                borderRadius: 2,
                bgcolor: "#139fcae8",
                p: 0.5,
              }}
            >
              <ArrowBackRoundedIcon sx={{ fill: "#FFF" }} />
            </Box>
            <Box
              onClick={() => navigate("/travel/dashboard")}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                borderRadius: 2,
                bgcolor: "#139fcae8",
                p: 0.5,
              }}
            >
              <HouseIcon sx={{ fill: "#FFF" }} />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <DirectionsCarFilledRoundedIcon sx={{ fill: "#139fcae8" }} />
              <Typography
                variant="body1"
                fontWeight={600}
                sx={{ color: "#139fcae8" }}
              >
                Vehicle Master
              </Typography>
            </Box>
          </Box>

          {/* Search Field */}
          <Box mb={isMobile ? 1 : 0}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search vehicle"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* All Vehicles List */}
          <Card
            sx={{
              mt: 2,
              flex: 1, // takes remaining height
              display: "flex",
              flexDirection: "column",
            }}
          >
            {selectedVehicleIds.length > 0 && (
              <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                p={1}
                onClick={() => {
                  const vehicleIDs = vehicles.filter((v) =>
                    selectedVehicleIds.includes(v.vehicle_id),
                  );
                  setVehicleToDelete(vehicleIDs);
                  setOpenDeleteDialogBox(true);
                }}
                sx={{
                  cursor: "pointer",
                  borderRadius: 1,
                  transition: "background-color 0.2s ease",

                  "&:hover": {
                    backgroundColor: "rgba(211, 47, 47, 0.08)", // error light bg
                  },

                  "&:hover .delete-text": {
                    color: "error.main",
                    fontWeight: 600,
                  },

                  "&:hover .delete-icon": {
                    color: "error.main",
                  },
                }}
              >
                <Typography
                  className="delete-text"
                  sx={{ fontSize: 14, color: "text.secondary", mr: 0.5 }}
                >
                  Delete All Selected Vehicles
                </Typography>

                <IconButton className="delete-icon" color="error" size="small">
                  <DeleteForeverRoundedIcon />
                </IconButton>
              </Stack>
            )}

            <CardContent
              sx={{
                flex: 1,
                overflowY: "auto", // ✅ enables scrolling
                p: 0, // optional: tighter table look
                ...hideScrollbar,
              }}
            >
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectAll}
                          onChange={handleSelectAll}
                        />
                      </TableCell>
                      <TableCell>Sl.No</TableCell>
                      <TableCell>Code</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Number</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {filteredVehicles.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          No vehicles {vehicles.length === 0 ? "" : "found"}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredVehicles.map((v, index) => (
                        <TableRow
                          key={v.vehicle_id}
                          selected={
                            selectedVehicle?.vehicle_id === v.vehicle_id
                          }
                          hover
                          sx={{ cursor: "pointer" }}
                          onClick={() => fetchOneVehicle(v.vehicle_id)}
                        >
                          <TableCell
                            padding="checkbox"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Checkbox
                              checked={selectedVehicleIds.includes(
                                v.vehicle_id,
                              )}
                              onChange={() => handleToggleVehicle(v)}
                            />
                          </TableCell>
                          <TableCell>{v.sl_no}</TableCell>{" "}
                          {/* <-- S.No based on index */}
                          <TableCell>{v.vehicle_code}</TableCell>
                          <TableCell>{v.vehicle_name}</TableCell>
                          <TableCell>{v.vehicle_number}</TableCell>
                          <TableCell>
                            <Chip size="small" label={v.vehicle_type} />
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={getStatusColor(v.vehicle_status)}
                              label={getStatusText(v.vehicle_status)}
                            />
                          </TableCell>
                          <TableCell>
                            <DeleteForeverRoundedIcon
                              sx={{ fontSize: 20, fill: "#F00" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setVehicleToDelete(v);
                                setOpenDeleteDialogBox(true);
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* RIGHT */}
        <Grid item xs={12} md={7} sx={{ order: { xs: 1, md: 2 } }}>
          <Card>
            <CardContent>
              {/* Vehicle Details & Block Vehicle */}
              <Grid container spacing={2}>
                {/* Vehicle Details */}
                <Grid item xs={12} md={6}>
                  <Typography fontWeight={600} mb={2}>
                    Vehicle Details
                  </Typography>

                  {/* Vehicle Code & Sl. No. */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: isMobile ? "column" : "row",
                      gap: 1.5,
                    }}
                  >
                    {/* Vehicle Code */}
                    <TextField
                      fullWidth
                      size="small"
                      label="Vehicle Code"
                      value={vehicleForm.vehicle_number.slice(-4)}
                      disabled={true}
                    />

                    {/* Sl. No. */}
                    <TextField
                      fullWidth
                      size="small"
                      label="Vehicle Sl. No. *"
                      value={vehicleForm.vehicle_sl_no}
                      onChange={(e) =>
                        setVehicleForm((p) => ({
                          ...p,
                          vehicle_sl_no: e.target.value,
                        }))
                      }
                    />
                  </Box>

                  {/* Name */}
                  <TextField
                    fullWidth
                    size="small"
                    label="Vehicle Name *"
                    sx={{ mt: 2 }}
                    value={vehicleForm.vehicle_name}
                    onChange={(e) =>
                      setVehicleForm((p) => ({
                        ...p,
                        vehicle_name: e.target.value,
                      }))
                    }
                  />

                  {/* Number */}
                  <TextField
                    fullWidth
                    size="small"
                    label="Vehicle Number *"
                    sx={{ mt: 2 }}
                    value={vehicleForm.vehicle_number}
                    onChange={(e) =>
                      setVehicleForm((p) => ({
                        ...p,
                        vehicle_number: e.target.value,
                      }))
                    }
                  />

                  {/* Type */}
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        fontWeight: 500,
                        display: "block",
                        mb: 0.75,
                      }}
                    >
                      Vehicle Type
                    </Typography>

                    {/* Search / Create input */}
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Search or create type..."
                      value={vehicleTypeSearch}
                      onChange={(e) => setVehicleTypeSearch(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && canCreateVehicleType) {
                          e.preventDefault();
                          const newType = vehicleTypeSearch.trim();
                          setAllVehicleTypes((prev) => [...prev, newType]);
                          setNewlyCreatedTypes(
                            (prev) => new Set([...prev, newType]),
                          );
                          setVehicleForm((prev) => ({
                            ...prev,
                            vehicle_type: newType,
                          }));
                          setVehicleTypeSearch("");
                        }
                      }}
                      sx={{ mb: 1 }}
                    />

                    {/* Type chips */}
                    <Stack direction="row" flexWrap="wrap" gap={0.75}>
                      {allVehicleTypes
                        .filter((t) =>
                          t
                            .toLowerCase()
                            .includes(vehicleTypeSearch.toLowerCase()),
                        )
                        .map((type) => {
                          const isSelected = vehicleForm.vehicle_type === type;
                          const isNew = newlyCreatedTypes.has(type);
                          return (
                            <Chip
                              key={type}
                              label={type}
                              size="small"
                              clickable
                              onClick={() =>
                                setVehicleForm((prev) => ({
                                  ...prev,
                                  vehicle_type: isSelected ? "" : type,
                                }))
                              }
                              onDelete={
                                isNew
                                  ? () => {
                                      setAllVehicleTypes((prev) =>
                                        prev.filter((t) => t !== type),
                                      );
                                      setNewlyCreatedTypes((prev) => {
                                        const next = new Set(prev);
                                        next.delete(type);
                                        return next;
                                      });
                                      if (vehicleForm.vehicle_type === type)
                                        setVehicleForm((prev) => ({
                                          ...prev,
                                          vehicle_type: "",
                                        }));
                                    }
                                  : undefined
                              }
                              sx={{
                                fontWeight: isSelected ? 600 : 500,
                                borderRadius: "10px",
                                color: isSelected ? "#fff" : PRIMARY,
                                backgroundColor: isSelected
                                  ? PRIMARY
                                  : `${PRIMARY}15`,
                                border: isSelected
                                  ? "1px solid transparent"
                                  : `1px solid ${PRIMARY}40`,
                                boxShadow: isSelected
                                  ? "0 4px 14px rgba(0,0,0,0.15)"
                                  : "none",
                                transition: "all 0.25s ease",
                                "& .MuiChip-deleteIcon": {
                                  color: isSelected
                                    ? "rgba(255,255,255,0.8)"
                                    : `${PRIMARY}90`,
                                  "&:hover": {
                                    color: isSelected ? "#fff" : PRIMARY,
                                  },
                                },
                                "&:hover": {
                                  backgroundColor: isSelected
                                    ? PRIMARY
                                    : `${PRIMARY}25`,
                                },
                              }}
                            />
                          );
                        })}

                      {/* Create new type chip */}
                      {canCreateVehicleType && (
                        <Chip
                          label={`+ Create "${vehicleTypeSearch.trim()}"`}
                          size="small"
                          clickable
                          color="success"
                          variant="outlined"
                          onClick={() => {
                            const newType = vehicleTypeSearch.trim();
                            setAllVehicleTypes((prev) => [...prev, newType]);
                            setNewlyCreatedTypes(
                              (prev) => new Set([...prev, newType]),
                            );
                            setVehicleForm((prev) => ({
                              ...prev,
                              vehicle_type: newType,
                            }));
                            setVehicleTypeSearch("");
                          }}
                        />
                      )}
                    </Stack>
                  </Box>
                </Grid>

                {/* Block Vehicle */}
                <Grid item xs={12} md={6}>
                  <Typography fontWeight={600}>
                    Service & Maintenance
                  </Typography>

                  <Stack direction="row" alignItems="center" mt={0.5}>
                    <Checkbox
                      checked={isBlocking}
                      onChange={(e) => setIsBlocking(e.target.checked)}
                    />
                    <Typography>Block Vehicle</Typography>
                  </Stack>

                  {isBlocking && (
                    <>
                      <Stack direction="row" spacing={2} mt={1}>
                        <TextField
                          type="date"
                          size="small"
                          label="From"
                          InputLabelProps={{ shrink: true }}
                          value={
                            vehicleForm.blocked_from
                              ? vehicleForm.blocked_from.split("T")[0]
                              : ""
                          }
                          onChange={(e) =>
                            setVehicleForm((p) => ({
                              ...p,
                              blocked_from: e.target.value,
                            }))
                          }
                        />

                        <TextField
                          type="date"
                          size="small"
                          label="To"
                          InputLabelProps={{ shrink: true }}
                          value={
                            vehicleForm.blocked_to
                              ? vehicleForm.blocked_to.split("T")[0]
                              : ""
                          }
                          onChange={(e) =>
                            setVehicleForm((p) => ({
                              ...p,
                              blocked_to: e.target.value,
                            }))
                          }
                        />
                      </Stack>

                      <TextField
                        fullWidth
                        size="small"
                        label="Reason"
                        sx={{ mt: 2 }}
                        value={vehicleForm.blocked_reason}
                        onChange={(e) =>
                          setVehicleForm((p) => ({
                            ...p,
                            blocked_reason: e.target.value,
                          }))
                        }
                      />
                    </>
                  )}
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* DOCUMENTS */}
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Documents
              </Typography>

              {formData.documents.length > 0 &&
                (isMobile ? (
                  <Stack spacing={1.5}>
                    {formData.documents.map((doc) => {
                      const docKey = doc.doc_id || doc.id;
                      return (
                        <Paper
                          key={docKey}
                          variant="outlined"
                          sx={{ p: 1.5, borderRadius: 2 }}
                        >
                          <Stack spacing={1}>
                            <TextField
                              size="small"
                              fullWidth
                              value={doc.name}
                              placeholder="Document name"
                              onChange={(e) => {
                                setFormData((p) => ({
                                  documents: p.documents.map((d) =>
                                    (d.doc_id || d.id) === docKey
                                      ? { ...d, name: e.target.value }
                                      : d,
                                  ),
                                }));
                              }}
                            />
                            <TextField
                              size="small"
                              fullWidth
                              value={doc.number}
                              placeholder="Number"
                              onChange={(e) => {
                                setFormData((p) => ({
                                  documents: p.documents.map((d) =>
                                    (d.doc_id || d.id) === docKey
                                      ? { ...d, number: e.target.value }
                                      : d,
                                  ),
                                }));
                              }}
                            />
                            <Button
                              variant="outlined"
                              size="small"
                              component="label"
                              fullWidth
                              sx={{
                                textTransform: "none",
                                justifyContent: "flex-start",
                              }}
                            >
                              {doc.fileName
                                ? doc.fileName.length > 35
                                  ? doc.fileName.substring(0, 35) + "..."
                                  : doc.fileName
                                : "Upload document file"}
                              <input
                                type="file"
                                hidden
                                accept="application/pdf,image/*"
                                onChange={async (e) => {
                                  const file = e.target.files[0];
                                  if (!file) return;
                                  const base64 = await convertFileToBase64(
                                    file,
                                  );
                                  setFormData((p) => ({
                                    documents: p.documents.map((d) =>
                                      (d.doc_id || d.id) === docKey
                                        ? {
                                            ...d,
                                            file: base64,
                                            fileName: file.name,
                                          }
                                        : d,
                                    ),
                                  }));
                                }}
                              />
                            </Button>
                            <TextField
                              size="small"
                              fullWidth
                              type="date"
                              value={doc.renewalDate || ""}
                              onChange={(e) => {
                                setFormData((p) => ({
                                  documents: p.documents.map((d) =>
                                    (d.doc_id || d.id) === docKey
                                      ? { ...d, renewalDate: e.target.value }
                                      : d,
                                  ),
                                }));
                              }}
                              InputLabelProps={{ shrink: true }}
                            />
                            <Box
                              display="flex"
                              justifyContent="flex-end"
                              gap={1}
                            >
                              <Tooltip title="View document">
                                <IconButton
                                  size="small"
                                  onClick={() => setViewerDoc(doc)}
                                  sx={{ color: PRIMARY }}
                                >
                                  <VisibilityRoundedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteDocument(docKey)}
                              >
                                <DeleteOutlineOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Stack>
                        </Paper>
                      );
                    })}
                  </Stack>
                ) : (
                  <Box sx={{ overflowX: "auto" }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ py: 1 }}>Name *</TableCell>
                          <TableCell sx={{ py: 1 }}>Number *</TableCell>
                          <TableCell sx={{ py: 1 }}>Upload</TableCell>
                          <TableCell sx={{ py: 1 }}>Renewal</TableCell>
                          <TableCell sx={{ py: 1 }}>View</TableCell>
                          <TableCell sx={{ py: 1 }} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {formData.documents.map((doc) => {
                          const docKey = doc.doc_id || doc.id;
                          return (
                            <TableRow key={docKey}>
                              <TableCell sx={{ py: 0.5 }}>
                                <TextField
                                  size="small"
                                  value={doc.name}
                                  placeholder="Document name"
                                  sx={{ minWidth: 120 }}
                                  onChange={(e) => {
                                    setFormData((p) => ({
                                      documents: p.documents.map((d) =>
                                        (d.doc_id || d.id) === docKey
                                          ? { ...d, name: e.target.value }
                                          : d,
                                      ),
                                    }));
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ py: 0.5 }}>
                                <TextField
                                  size="small"
                                  value={doc.number}
                                  placeholder="Number"
                                  sx={{ minWidth: 100 }}
                                  onChange={(e) => {
                                    setFormData((p) => ({
                                      documents: p.documents.map((d) =>
                                        (d.doc_id || d.id) === docKey
                                          ? { ...d, number: e.target.value }
                                          : d,
                                      ),
                                    }));
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ py: 0.5 }}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  component="label"
                                  sx={{ textTransform: "none" }}
                                >
                                  {doc.fileName
                                    ? doc.fileName.substring(0, 15) + "..."
                                    : "Upload"}
                                  <input
                                    type="file"
                                    hidden
                                    accept="application/pdf,image/*"
                                    onChange={async (e) => {
                                      const file = e.target.files[0];
                                      if (!file) return;
                                      const base64 = await convertFileToBase64(
                                        file,
                                      );
                                      setFormData((p) => ({
                                        documents: p.documents.map((d) =>
                                          (d.doc_id || d.id) === docKey
                                            ? {
                                                ...d,
                                                file: base64,
                                                fileName: file.name,
                                              }
                                            : d,
                                        ),
                                      }));
                                    }}
                                  />
                                </Button>
                              </TableCell>
                              <TableCell sx={{ py: 0.5 }}>
                                <TextField
                                  size="small"
                                  type="date"
                                  value={doc.renewalDate || ""}
                                  sx={{ minWidth: 130 }}
                                  onChange={(e) => {
                                    setFormData((p) => ({
                                      documents: p.documents.map((d) =>
                                        (d.doc_id || d.id) === docKey
                                          ? {
                                              ...d,
                                              renewalDate: e.target.value,
                                            }
                                          : d,
                                      ),
                                    }));
                                  }}
                                  InputLabelProps={{ shrink: true }}
                                />
                              </TableCell>
                              <TableCell sx={{ py: 0.5 }}>
                                <Tooltip title="View document">
                                  <IconButton
                                    size="small"
                                    onClick={() => setViewerDoc(doc)}
                                    sx={{ color: PRIMARY }}
                                  >
                                    <VisibilityRoundedIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                              <TableCell sx={{ py: 0.5 }}>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteDocument(docKey)}
                                >
                                  <DeleteOutlineOutlinedIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Box>
                ))}

              {/* Add Document Button */}
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() =>
                  setFormData((p) => ({
                    documents: [
                      ...p.documents,
                      {
                        id: Date.now(),
                        name: "",
                        number: "",
                        file: null,
                        fileName: "",
                        renewalDate: "",
                      },
                    ],
                  }))
                }
                size="small"
                sx={{ mt: 1.5, textTransform: "none" }}
              >
                Add Document
              </Button>

              {/* Buttons */}
              <Stack
                direction="row"
                justifyContent="flex-end"
                spacing={2}
                mt={1}
              >
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={isLoading}
                  sx={{ minWidth: 140 }}
                >
                  {isLoading ? (
                    <Stack
                      direction={isMobile ? "column" : "row"}
                      spacing={2}
                      alignItems={isMobile ? "stretch" : "center"}
                    >
                      <CircularProgress size={18} color="inherit" />
                      <Typography variant="body2">
                        {selectedVehicle ? "Saving..." : "Adding..."}
                      </Typography>
                    </Stack>
                  ) : selectedVehicle ? (
                    "Save Changes"
                  ) : (
                    "Add Vehicle"
                  )}
                </Button>

                <Button variant="outlined" onClick={resetAllForms}>
                  Cancel
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* DELETE CONFIRMATION DIALOG */}
      <Dialog
        fullWidth
        maxWidth="xs"
        open={openDeleteDialogBox}
        onClose={() => setOpenDeleteDialogBox(false)}
      >
        <DialogTitle>Delete Vehicle(s)</DialogTitle>

        <DialogContent>
          <Typography>
            Are you sure you want to delete the following vehicle(s)?
          </Typography>

          {Array.isArray(vehicleToDelete) ? (
            vehicleToDelete.map((v) => (
              <Typography key={v.vehicle_id} sx={{ fontWeight: 600 }}>
                • {v.vehicle_name} ({v.vehicle_number})
              </Typography>
            ))
          ) : (
            <Typography sx={{ fontWeight: 600 }}>
              {vehicleToDelete?.vehicle_name} ({vehicleToDelete?.vehicle_number}
              )
            </Typography>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setOpenDeleteDialogBox(false)}
            variant="outlined"
          >
            Cancel
          </Button>

          <Button
            color="error"
            variant="contained"
            disabled={isLoading}
            onClick={handleConfirmDeleteVehicle}
          >
            {isLoading ? (
              <CircularProgress size={18} color="inherit" />
            ) : Array.isArray(vehicleToDelete) ? (
              "Delete All"
            ) : (
              "Delete"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Document Viewer */}
      <DocumentViewerDialog
        doc={viewerDoc}
        onClose={() => setViewerDoc(null)}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackBarConfig.open}
        autoHideDuration={3000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{
          "&.MuiSnackbar-root": {
            bottom: "56px !important", // force override snackbar position
          },
        }}
      >
        {/* You can use Alert for styled messages */}
        <Alert
          onClose={closeSnackbar}
          severity={snackBarConfig.severity}
          variant="filled"
        >
          {snackBarConfig.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
